/**
 * Cienki wrapper na endpointy SENT w urzad-api.
 *
 * Użycie w komponencie Vue:
 *   import { createSentApi, buildSent105FromOrder } from '@/services/sentApi'
 *   const api = createSentApi(this.$store.state.apiComplaiaLinkLocal)
 *   const result = await api.sendSent105(buildSent105FromOrder(order, route))
 *
 * `baseUrl` powinien wskazywać na ścieżkę kończącą się na `/api/private`
 * (taki format jest w store: apiComplaiaLink / apiComplaiaLinkLocal).
 *
 * Wszystkie zapytania noszą nagłówek `x-app-id: urzad_1768824824` (zgodne z KSeF).
 */
import axios from 'axios'
import moment from 'moment'

const APP_ID = 'urzad_1768824824'
const PROFILE = 'demo' // wszystkie wysyłki na profilu demo zgodnie z wymaganiami

// Adresy w Firestore są jednolinijkowe ("Targowa 9", "Targowa, 9", "Plac Piłsudskiego 12/4"),
// PUESC XSD wymaga osobno Street + HouseNumber. Brak HouseNumber = NPP (rejekcja schematu).
// Wyciągamy ostatnią grupę zaczynającą się od cyfry jako numer domu, reszta → ulica.
// Brak numeru → throw, bo PUESC odrzuci wysyłkę. Walidacja UI powinna to złapać wcześniej —
// to safety net dla starych zamówień / scenariuszy z pominiętą walidacją.
function _splitAddress(raw) {
  const s = String(raw || '').trim()
  if (!s) throw new Error('SENT: adres dostawy jest pusty — uzupełnij i ponów wysyłkę.')
  const m = s.match(/^(.*?)[\s,]+([0-9][0-9A-Za-z\/\-]*)\s*$/)
  if (!m) {
    throw new Error(`SENT: adres "${s}" nie zawiera numeru domu wymaganego przez PUESC. Popraw adres dostawy (np. "Targowa 9").`)
  }
  return { street: m[1].replace(/[\s,]+$/, '').trim(), houseNumber: m[2].trim() }
}

// Czysta funkcja walidacji adresu — używana w UI modali (admin + client) przed
// otwarciem submit. Spójne z _splitAddress, żeby walidacja UI nie rozjeżdżała się
// z safety-net'em po stronie wysyłki.
export function isValidDeliveryAddress(raw) {
  const s = String(raw || '').trim()
  if (!s) return false
  return /^(.*?)[\s,]+([0-9][0-9A-Za-z\/\-]*)\s*$/.test(s)
}

// SENT_105 XSD ma DWA różne typy dla pól dat (potwierdzone NPP od PUESC z 2026-05-06):
//   • PlannedStartCarriageDate → xsd:dateTime → "YYYY-MM-DDTHH:mm:ss"
//   • StartTransportDate       → xsd:date     → "YYYY-MM-DD"
//   • EndTransportDate         → xsd:date     → "YYYY-MM-DD"
// Trasa w Firestore trzyma plannedStart/End jako { unix } (czasem fallback .date.seconds / ._seconds).
function _slotUnix(slot) {
  if (!slot) return null
  return slot.unix || slot.date?.seconds || slot.date?._seconds || null
}
function _slotToIsoDateTime(slot) {
  const u = _slotUnix(slot)
  return u ? moment.unix(u).format('YYYY-MM-DDTHH:mm:ss') : undefined
}
function _slotToIsoDate(slot) {
  const u = _slotUnix(slot)
  return u ? moment.unix(u).format('YYYY-MM-DD') : undefined
}

// PUESC GeoLocatorNumber XSD pattern (z NPP od 2026-05-06):
//   [ZAUM][0-9]{2}[ABCEFGHKMNPRSTWXYZ]{2}[0-9]{2}[ABCEFGHKMNPRSTWXYZ]{2}-[0-9]{1}
// Takich numerów nie da się "wymyślić" — to formalny identyfikator nadawany przez SISC.
// Większość naszych zapisów w Firestore (np. "Z00-CR69KC-7", "GPS/2") nie pasuje.
// Wysyłamy tylko gdy faktycznie pasuje do wzoru — inaczej omijamy pole (PUESC akceptuje
// brak GeoLocatorNumber dla typowego transportu).
const PUESC_GEO_LOCATOR_RE = /^[ZAUM][0-9]{2}[ABCEFGHKMNPRSTWXYZ]{2}[0-9]{2}[ABCEFGHKMNPRSTWXYZ]{2}-[0-9]{1}$/
function _validGeoLocator(raw) {
  const s = String(raw || '').trim()
  if (!s) return undefined
  return PUESC_GEO_LOCATOR_RE.test(s) ? s : undefined
}

function _client(baseUrl) {
  const cleanedBase = String(baseUrl || '').replace(/\/+$/, '')
  return axios.create({
    baseURL: cleanedBase,
    headers: { 'x-app-id': APP_ID, 'Content-Type': 'application/json' },
    timeout: 60000, // SENT_105 wysyłka do PUESC może chwilę trwać
  })
}

export function createSentApi(baseUrl) {
  const c = _client(baseUrl)
  return {
    /**
     * POST /sent/sendSent105
     * payload = { profile, monitoring, sent105 }
     * Zwraca StandardResponse (success / error / payload).
     */
    sendSent105: (payload) => c.post('/sent/sendSent105', payload).then(r => r.data),

    /**
     * POST /sent/markDelivered/:documentId
     * deliveredAt opcjonalny ISO timestamp; default = teraz po stronie urzad-api.
     */
    markDelivered: (documentId, deliveredAt = null) =>
      c.post(`/sent/markDelivered/${encodeURIComponent(documentId)}`, deliveredAt ? { deliveredAt } : {}).then(r => r.data),

    /**
     * GET /sent/getSentStatus/:documentId
     * Zwraca { status, sysRef, sentRefNumber, callback, ... } z MongoDB urzad-api.
     */
    getSentStatus: (documentId) =>
      c.get(`/sent/getSentStatus/${encodeURIComponent(documentId)}`).then(r => r.data),

    /**
     * GET /sent/messages/:documentId
     * Lista zwrotek (SENT_115 / SENT_500 / SENT_112 / UPP / NPP) z metadanymi.
     */
    listMessages: (documentId) =>
      c.get(`/sent/messages/${encodeURIComponent(documentId)}`).then(r => r.data),

    /**
     * GET /sent/messages/:documentId/:idx/raw
     * Pobranie surowego pliku XML lub PDF (Blob).
     * Użycie w przeglądarce: window.open(URL.createObjectURL(blob))
     */
    getMessageRaw: (documentId, idx) =>
      c.get(`/sent/messages/${encodeURIComponent(documentId)}/${idx}/raw`,
        { responseType: 'blob' }).then(r => r.data),

    /** GET /sent/cronHealth — telemetria cron pullInbox. Frontend pokazuje banner. */
    getCronHealth: () => c.get('/sent/cronHealth').then(r => r.data),
  }
}

/**
 * Czysta funkcja składania payloadu SENT_105 z obiektu zamówienia + trasy.
 * urzad-api samo dorabia goodsSender + carrier + placeOfLoading z profile.depot
 * (DEMO / LAURON), więc tutaj wpisujemy tylko: dane odbiorcy, towar, datę, pojazd.
 *
 * @param {object} order - dokument FuelOrders (lub freshSnapshot z trasy)
 * @param {object} route - dokument Routes (do dat planowanych i danych kierowcy/pojazdu)
 */
export function buildSent105FromOrder(order, route) {
  const recipient = order?.client?.invoiceAddress || {}
  const recipientContact = order?.client?.contact || {}
  const fuel = order?.orderedFuel || {}
  const delivery = order?.deliveryAddress || {}
  const coords = delivery?.coordinates || {}
  const driverName = String(route?.driver?.name || '').trim()
  const driverSurname = (() => {
    // route.driver może być "Imię Nazwisko" lub osobne pola — robimy split jako fallback
    const parts = driverName.split(/\s+/)
    return parts.length >= 2 ? parts.slice(1).join(' ') : ''
  })()
  const driverFirstName = driverName.split(/\s+/)[0] || ''

  // Numer telefonu: SENT XSD wymaga 9 cyfr bez prefixu. Wyciągamy cyfry.
  const phone = String(recipientContact?.phone || '').replace(/\D/g, '').slice(-9)

  return {
    profile: PROFILE,
    monitoring: {
      recipientEmail: recipientContact?.email || '',
      recipientName: recipient?.name || '',
      recipientNip: recipient?.nip || '',
      // additionalEmails / escalationEmails / internalAlertEmail — można dorzucić z konfiguracji
    },
    sent105: {
      typeOfTransport: '1', // 1=drogowy
      typeOfDeclaration: '1',
      // goodsSender, carrier, transport.placeOfLoading — auto-fill po stronie urzad-api z profile.depot
      goodsRecipient: (() => {
        const addrSrc = delivery?.address || recipient?.address || ''
        const { street, houseNumber } = _splitAddress(addrSrc)
        return {
          traderInfo: {
            traderName: recipient?.name || '',
            traderIdentityType: 'NIP',
            traderIdentityNumber: String(recipient?.nip || '').replace(/\D/g, ''),
          },
          traderAddress: {
            street,
            houseNumber,
            city: delivery?.city || recipient?.city || '',
            country: 'PL',
            postalCode: delivery?.postCode || recipient?.postCode || '',
          },
        }
      })(),
      meansOfTransport: {
        truckOrTrainNumber: route?.vehicle?.licensePlate || '',
        trailerOrWagonNumber: route?.trailer?.licensePlate || '',
        // GeoLocatorNumber tylko jeśli rzeczywiście pasuje do PUESC pattern.
        // Inaczej PUESC odrzuca z `cvc-pattern-valid` na `LocatorNumber`.
        geoLocatorNumber: _validGeoLocator(route?.vehicle?.gpsTrackerNumber),
        permitRoad: 'NIEWYMAGANE',
        notificationSMSPhone: phone || undefined,
      },
      transport: {
        // PlannedStart = xsd:dateTime (z czasem), Start/EndTransportDate = xsd:date (sama data).
        plannedStartCarriageDate: _slotToIsoDateTime(route?.plannedStart),
        startTransportDate: _slotToIsoDate(route?.plannedStart),
        endTransportDate: _slotToIsoDate(route?.plannedEnd),
        // placeOfLoading auto-fill po stronie urzad-api
        placeOfDelivery: (() => {
          const { street, houseNumber } = _splitAddress(delivery?.address)
          return {
            street,
            houseNumber,
            city: delivery?.city || '',
            country: 'PL',
            postalCode: delivery?.postCode || '',
            codeTERC: '',                                // TODO: docelowo z mapy regionów
            latitude: coords.latitude != null ? String(coords.latitude) : '',
            longitude: coords.longitude != null ? String(coords.longitude) : '',
          }
        })(),
      },
      goodsInformation: [
        {
          elementNumber: '1',
          codeCnClassification: '2710',                // nagłówek CN dla paliw
          goodsName: fuel?.fuelName || '',
          amountOfGoods: String(fuel?.quantityLiters || ''),
          unitOfMeasure: 'l',
          vatRate: '23',
        },
      ],
      purposeOfTheGoods: 'ZPO',
      goodsTransportDocuments: [
        {
          typeOfTransportDocument: 'LIST',
          numberOfTransportDocument: order?.id || '',  // ten sam numer co w PDF list przewozowy
        },
      ],
      documentId: order?.id || '',
      responseAddress: { wsFromSISC: true },
      statements: {
        statement1: true,
        firstName: driverFirstName,
        lastName: driverSurname,
      },
      additionalStatements: { statement2: true, statement3: true },
    },
  }
}
